import type { Media } from '@wineryxplorer/shared';
import { TextOverlay } from './TextOverlay';
import { CarouselControls } from './CarouselControls';
import { PaginationDots } from './PaginationDots';
import { CarouselImage } from './CarouselImage';
import { DebugInfo } from './DebugInfo';
import { useCarouselLogic } from './useCarouselLogic';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

const WINERY_PLACEHOLDER = '/assets/placeholders/winery-placeholder.svg';

interface HeroCarouselProps {
  images: Media[];
  venueName: string;
  venueType: string;
  autoAdvanceInterval?: number; // Default 4000ms
  transitionDuration?: number; // Default 800ms
  className?: string;
  showControls?: boolean; // Default false
  withPaginationDots?: boolean; // Default false
  debugMode?: boolean; // Default false
  withTextOverlay?: boolean;
}

export const HeroCarousel = ({
  images,
  venueName,
  venueType,
  autoAdvanceInterval = 4000,
  transitionDuration = 800,
  className = '',
  showControls = false,
  withPaginationDots = false,
  debugMode = false,
  withTextOverlay = false
}: HeroCarouselProps) => {
  const {
    currentIndex,
    isTransitioning,
    isPaused,
    isManuallyPaused,
    prefersReducedMotion,
    isTouching,
    imageStates,
    cloudName,
    availableImages,
    carouselRef,
    announceRef,
    goToNext,
    goToPrevious,
    goToSlide,
    toggleManualPause,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handleKeyDown,
  } = useCarouselLogic({
    images,
    autoAdvanceInterval,
    transitionDuration,
    venueName,
  });

  // Handle no images or all images failed with enhanced fallback
  if (images.length === 0 || availableImages.length === 0) {

    return (
      <div
        className={`relative w-full h-64 md:h-96 bg-gray-100 overflow-hidden ${className}`}
        role="img"
        aria-label={`${venueName} - ${images.length === 0 ? 'no hero images available' : 'hero images failed to load'}`}
      >
        {/* Drawn vineyard scene, shared with mobile. Master lives at
            docs/design/winery-placeholder.svg — re-copy it here if it changes. */}
        <img
          src={WINERY_PLACEHOLDER}
          alt=""
          className="w-full h-full object-cover"
        />

        {/* Overlay message for failed loads */}
        {images.length > 0 && availableImages.length === 0 && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <div className="bg-white/90 rounded-lg p-4 text-center max-w-xs">
              <div className="text-gray-700 text-sm font-medium mb-1">
                Images temporarily unavailable
              </div>
              <div className="text-gray-500 text-xs">
                Please try refreshing the page
              </div>
            </div>
          </div>
        )}
        
       {/* { withTextOverlay && <TextOverlay venueName={venueName} venueType={venueType} /> } */}
      </div>
    );
  }

  // Single image (or only one available image)
  if (availableImages.length === 1) {
    const singleImage = availableImages[0];
    const imageState = imageStates.get(singleImage.id);
    
    return (
      <div 
        className={`relative w-full h-64 md:h-96 overflow-hidden ${className}`}
        role="img"
        aria-label={`${venueName} hero image`}
      >
        {/* Blur placeholder */}
        {imageState?.blurUrl && !imageState.loaded && (
          <img
            src={imageState.blurUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-110"
          />
        )}
        
        {/* Main image */}
        {imageState?.url && (
          <ImgWithSkeleton
            src={imageState.url}
            alt={`${venueName} hero image`}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageState.loaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
        
       {/* { withTextOverlay && <TextOverlay venueName={venueName} venueType={venueType} /> } */}
      </div>
    );
  }

  return (
    <div
      ref={carouselRef}
      className={`relative w-full h-64 md:h-96 overflow-hidden ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label={`${venueName} hero image carousel`}
      aria-describedby="carousel-instructions"
      style={{ touchAction: 'pan-y' }} // Allow vertical scrolling but handle horizontal swipes
    >
      {/* Image Container with Cross-Dissolve */}
      <div 
        className="relative w-full h-full"
        role="img"
        aria-label={`${venueName} hero image ${currentIndex + 1} of ${images.length}`}
        aria-live="polite"
      >
        {images.map((image, index) => {
          const imageState = imageStates.get(image.id);
          const isCurrentImage = index === currentIndex;
          const isVisible = isCurrentImage && !imageState?.error;
          
          return (
            <CarouselImage
              key={image.id}
              image={image}
              index={index}
              venueName={venueName}
              isCurrentImage={isCurrentImage}
              isVisible={isVisible}
              imageState={imageState}
              cloudName={cloudName}
              prefersReducedMotion={prefersReducedMotion}
              transitionDuration={transitionDuration}
            />
          );
        })}
      </div>

      {/* Navigation Controls - conditionally rendered */}
      {showControls && (
        <CarouselControls
          onPrevious={goToPrevious}
          onNext={goToNext}
          onTogglePause={toggleManualPause}
          isTransitioning={isTransitioning}
          isManuallyPaused={isManuallyPaused}
          currentIndex={currentIndex}
          totalImages={images.length}
        />
      )}

      {/* Navigation Dots - only show for available images */}
      {withPaginationDots && (
        <PaginationDots
          images={images}
          currentIndex={currentIndex}
          imageStates={imageStates}
          isTransitioning={isTransitioning}
          onGoToSlide={goToSlide}
        />
      )}

      {/* Text Overlay with Scrim */}
      {/* <TextOverlay venueName={venueName} venueType={venueType} /> */}

      {/* Touch interaction indicator */}
      {isTouching && (
        <div className="absolute inset-0 bg-black/10 pointer-events-none transition-opacity duration-150" />
      )}

      {/* Screen reader announcements */}
      <div
        ref={announceRef}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      />

      {/* Screen reader instructions */}
      <div id="carousel-instructions" className="sr-only">
        Use arrow keys to navigate between images, spacebar to pause or resume auto-advance, 
        Home to go to first image, End to go to last image.
        {prefersReducedMotion && ' Auto-advance is disabled due to reduced motion preference.'}
        {isManuallyPaused && ' Carousel is currently paused.'}
      </div>

      {/* Debug info */}
      {debugMode && (
        <DebugInfo
          currentIndex={currentIndex}
          images={images}
          availableImagesCount={availableImages.length}
          isPaused={isPaused}
          isTransitioning={isTransitioning}
          isTouching={isTouching}
          cloudName={cloudName}
          imageStates={imageStates}
        />
      )}
    </div>
  );
};